import { useMemo } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { useImpersonation } from '@/hooks/useImpersonation';

/**
 * Hook that returns the effective companyId for data queries.
 * When impersonating, returns the impersonated company's ID.
 * Otherwise, returns the current user's companyId.
 *
 * Use this hook instead of user?.companyId in pages that need
 * to respect impersonation context.
 */
export function useEffectiveCompanyId() {
  const { user } = useAuth();
  const { isImpersonating, targetUser } = useImpersonation();

  const effectiveCompanyId = useMemo(() => {
    if (isImpersonating && targetUser?.companyId) {
      return targetUser.companyId;
    }
    return user?.companyId || null;
  }, [isImpersonating, targetUser?.companyId, user?.companyId]);

  const effectiveCompanyName = useMemo(() => {
    if (isImpersonating && targetUser?.companyName) {
      return targetUser.companyName;
    }
    return null; // The actual company name would need to be fetched
  }, [isImpersonating, targetUser?.companyName]);

  return {
    effectiveCompanyId,
    effectiveCompanyName,
    isImpersonating,
    // Original user info is still available if needed
    actualUserId: user?.uid,
    actualCompanyId: user?.companyId,
  };
}
